// Copyright ©️ 2026 eVolve MEP, LLC

import '../Assigning/spooling.css';

import { useState } from 'react';

import { FLAT_BLACK_FILLER } from '@evolvemep/api-ts';

import { isNil } from 'helpers/isNotNil';
import { SpoolAccordion } from 'modules/Shop/Spooling/Assigning/SpoolAccordion';
import type { SpoolRequestEntry } from 'modules/Shop/Spooling/Assigning/useSpoolRequestsLoader';
import { useSpoolRequestsViewerState } from 'modules/Shop/Spooling/hooks/useSpoolRequestsViewerState';

import { DeleteRequestModal, type SpoolRequestAction, UpdateRequestModal } from './SpoolRequestActionModals';

type Props = {
  requestingNewSpools: boolean;
  filteredRequestedSpools: SpoolRequestEntry[];
  colorKeyEnabled: boolean;
};

export const RequestedSpools = ({ filteredRequestedSpools, requestingNewSpools, colorKeyEnabled }: Props) => {
  const {
    spoolRequests,
    colorScheme,
    visibleSpoolRequests,
    toggleSpoolRequestVisibility,
    toggleAllVisible,
    updateSpoolColor,
    hasSpoolingAccess,
    loadingSpoolRequests,
  } = useSpoolRequestsViewerState();
  const [spoolAction, setSpoolAction] = useState<SpoolRequestAction | null>(null);
  if (!hasSpoolingAccess) return null;
  return (
    <>
      <SpoolAccordion<SpoolRequestEntry, 'Rename' | 'Delete request'>
        label="Requested Spools"
        itemValue="requestedSpools"
        spools={filteredRequestedSpools}
        getRenderableSpool={([_, rs]) => rs}
        loading={loadingSpoolRequests}
        visibilityProps={{
          disabled: requestingNewSpools || isNil(spoolRequests),
          getVisibility: ([spoolId]) => visibleSpoolRequests.includes(spoolId),
          toggle: ([spoolId]) => toggleSpoolRequestVisibility(spoolId),
          toggleAll: toggleAllVisible,
        }}
        colorProps={{
          disabled: !colorKeyEnabled,
          getColor: ([spoolId]) => colorScheme[spoolId] ?? FLAT_BLACK_FILLER,
          updateColor: ([spoolId], newColor) => updateSpoolColor(spoolId, newColor),
        }}
        getMenuProps={([spoolId, spool]) => ({
          disabled: requestingNewSpools,
          options: ['Rename', 'Delete request'],
          onMenuItemSelected: (opt) => setSpoolAction({ action: opt, spoolId, spool }),
        })}
      />
      <DeleteRequestModal spoolAction={spoolAction} onClose={() => setSpoolAction(null)} />
      <UpdateRequestModal spoolAction={spoolAction} onClose={() => setSpoolAction(null)} />
    </>
  );
};
